import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateValue, formatDateTimeValue } from '@/lib/utils';

import { useDetalleCodigo } from '../hooks/use-calibracion';
import { estadoCodigoBadge, estadoCodigoLabel, vigenciaCodigo } from '../lib/calibracion-format';

interface CodigoHistorialDialogProps {
  /** Código seleccionado; `null` cierra el diálogo. */
  codigoId: number | null;
  onClose: () => void;
}

/** Detalle de un código con su historial de uso. */
export function CodigoHistorialDialog({ codigoId, onClose }: CodigoHistorialDialogProps) {
  const detalle = useDetalleCodigo(codigoId);
  const codigo = detalle.data?.codigo;
  const historial = detalle.data?.historial ?? [];

  return (
    <Dialog
      open={codigoId !== null}
      onClose={onClose}
      title={codigo ? `Código ${codigo.codigo}` : 'Detalle del código'}
      description="Vigencia, usos registrados y quién lo utilizó."
      size="md"
    >
      {detalle.isPending ? (
        <Skeleton className="h-40 rounded-card" />
      ) : detalle.isError || !codigo ? (
        <p className="py-6 text-center text-sm text-ink-500">
          No se pudo cargar el detalle del código.
        </p>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={estadoCodigoBadge(codigo.estado_efectivo)}>
              {estadoCodigoLabel(codigo.estado_efectivo)}
            </Badge>
            <span className="text-xs text-ink-500">{vigenciaCodigo(codigo)}</span>
            {codigo.estado !== codigo.estado_efectivo ? (
              <Badge variant="outline">Guardado como {estadoCodigoLabel(codigo.estado)}</Badge>
            ) : null}
          </div>

          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Usos
              </dt>
              <dd className="mt-0.5 font-medium text-ink-800">
                {codigo.usos_restantes} disponibles de {codigo.cantidad_usos}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Duración
              </dt>
              <dd className="mt-0.5 font-medium text-ink-800">
                {codigo.duracion_dias} {codigo.duracion_dias === 1 ? 'día' : 'días'}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Creado
              </dt>
              <dd className="mt-0.5 font-medium text-ink-800">
                {formatDateTimeValue(codigo.fecha_creacion)}
                {codigo.creado_por_nombre ? ` · ${codigo.creado_por_nombre}` : ''}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Expira
              </dt>
              <dd className="mt-0.5 font-medium text-ink-800">
                {codigo.fecha_expiracion ? formatDateTimeValue(codigo.fecha_expiracion) : 'Sin fecha'}
              </dd>
            </div>
            {codigo.descripcion ? (
              <div className="sm:col-span-2">
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                  Descripción
                </dt>
                <dd className="mt-0.5 text-ink-700">{codigo.descripcion}</dd>
              </div>
            ) : null}
          </dl>

          <div className="border-t border-ink-100 pt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Historial de uso
            </p>

            {historial.length === 0 ? (
              <p className="mt-2 text-sm text-ink-500">Todavía no se ha usado este código.</p>
            ) : (
              <ul className="mt-2 divide-y divide-ink-100">
                {historial.map((uso) => (
                  <li key={uso.id} className="flex flex-wrap justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-800">
                        {uso.usuario_nombre || 'Usuario desconocido'}
                      </p>
                      <p className="text-xs text-ink-500">
                        {uso.calibration_id
                          ? `Calibración #${uso.calibration_id}`
                          : 'Sin calibración asociada'}
                        {uso.notas ? ` · ${uso.notas}` : ''}
                      </p>
                    </div>
                    <span className="text-xs text-ink-400">
                      {formatDateValue(uso.fecha_uso)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
